/**
 * Alpha DNA's hero for the warehouse: the card floating low over the wet floor
 * at the end of a dark colonnade (thin, dim light strips that mirror in the
 * floor), lit by one spotlight from above. Framing: straight on and level from
 * just above the floor, a longer lens so the hall reads as a corridor; a lens
 * shift puts the card in the top half, over the copy, with verticals straight.
 * Kept dark and crisp: nothing bright enough to bloom except the card's light.
 */
import * as THREE from 'three';
import { CARD_H, CARD_W, createCard } from './card';
import { b2t, NO_MIRROR_LAYER } from './warehouse';

const SPOT_CD = 48; // card spotlight, candela
const FLOOR_CD = 90; // a faint pool on the wet floor in front of the card (the floor stays dark)
const FOOT = 0.22; // the card floats this high over the floor (its bottom edge), metres
const CARD_Y = FOOT + CARD_H / 2; // card centre height
const CARD_DEPTH = 10.2; // metres into the hall (Blender y)
const CAM_HEIGHT = 0.3; // eye just above the card's foot: level, a touch from below
// where the card's foot lands in the frame (0 top .. 1 bottom), and how tall it is there
const LAND = { foot: 0.5, fill: 0.43, vfov: 40 }; // wide screens: the copy sits under the card
const PORT = { foot: 0.47, fill: 0.38, vfov: 48 }; // phones: a longer column of copy below
// the colonnade: dark pillars with a light strip on their inner face, in two rows leading back to
// the card; the strips mirror in the wet floor (Blender metres from the card, along the hall)
const COLS = { half: 3.4, halfPortrait: 1.7, first: 1.6, step: 2.7, count: 6, w: 0.32, h: 3.6 };
// under the bloom threshold: crisp thin lines, not glowing halos
const STRIP_WHITE = new THREE.Color(0.7, 0.82, 1).multiplyScalar(1.5);

export async function createCardHero(ctx, initial) {
  const { scene } = ctx;
  const cardPos = b2t(ctx.manifest.card[0], CARD_DEPTH, CARD_Y);
  const card = await createCard(ctx.renderer, initial);
  card.group.position.copy(cardPos);
  scene.add(card.group);
  // no reflection of the card in the wet floor: it sat right behind the copy
  card.group.traverse((o) => o.layers.set(NO_MIRROR_LAYER));

  const disposables = [];

  const colGeo = new THREE.BoxGeometry(COLS.w, COLS.h, COLS.w);
  const colMat = new THREE.MeshStandardMaterial({
    color: 0x0b0f17,
    roughness: 0.45,
    metalness: 0.6,
    envMapIntensity: 0.5,
  });
  const stripGeo = new THREE.BoxGeometry(0.01, COLS.h * 0.72, 0.04);
  const stripMat = new THREE.MeshBasicMaterial({ color: STRIP_WHITE });
  const cols = [];
  for (const side of [-1, 1]) {
    for (let i = 0; i < COLS.count; i++) {
      const col = new THREE.Mesh(colGeo, colMat);
      cols.push({ mesh: col, side });
      col.position.set(cardPos.x + side * COLS.half, COLS.h / 2, cardPos.z - COLS.first - i * COLS.step);
      const strip = new THREE.Mesh(stripGeo, stripMat);
      strip.position.set(-side * (COLS.w / 2 + 0.006), -COLS.h * 0.06, 0); // on the face toward the aisle
      col.add(strip);
      scene.add(col);
    }
  }
  disposables.push(colGeo, colMat, stripGeo, stripMat);

  const lampAt = cardPos.clone().add(new THREE.Vector3(0, 3.0, 0.6));
  const cardSpot = new THREE.SpotLight(0xdde8ff, 0, 0, THREE.MathUtils.degToRad(19), 0.75, 2);
  cardSpot.position.copy(lampAt);
  cardSpot.target.position.copy(cardPos).add(new THREE.Vector3(0, -0.2, 0));
  // the floor pool is lit from in front of the card, aimed straight down, so its cone
  // never reaches the card itself (it used to, and washed the print out)
  const floorSpot = new THREE.SpotLight(0xd8e6ff, 0, 0, THREE.MathUtils.degToRad(28), 0.9, 2);
  floorSpot.position.set(cardPos.x, 3.2, cardPos.z + 2.0);
  floorSpot.target.position.set(cardPos.x, 0, cardPos.z + 1.6);
  scene.add(cardSpot, cardSpot.target, floorSpot, floorSpot.target);

  return {
    // a few motes glinting in the card's light (no visible shaft: it hazed the hall)
    shafts: [{ x: cardPos.x, z: cardPos.z, r: 0.9 }],
    dustBox: new THREE.Box3(
      new THREE.Vector3(cardPos.x - 2, 0.2, cardPos.z - 2),
      new THREE.Vector3(cardPos.x + 2, 3.2, cardPos.z + 1.2),
    ),
    dustCount: 150,

    frame(aspect, fr) {
      // straight on and level; the card is sized by its height in the frame (narrow screens back
      // off so it never fills more than 80% of the width), then a lens shift lands its foot
      const L = aspect < 1 ? PORT : LAND;
      // a tall frame is narrow: the two rows close in so the corridor still shows either side
      const half = aspect < 1 ? COLS.halfPortrait : COLS.half;
      for (const c of cols) c.mesh.position.x = cardPos.x + c.side * half;
      const tv = Math.tan(THREE.MathUtils.degToRad(L.vfov / 2));
      const dist = Math.max(CARD_H / (L.fill * 2 * tv), CARD_W / (0.8 * 2 * tv * aspect));
      fr.fovTo = L.vfov;
      fr.camTo.set(cardPos.x, CAM_HEIGHT, cardPos.z + dist);
      fr.lookTo.set(cardPos.x, CAM_HEIGHT, cardPos.z);
      fr.shiftTo = 0.5 - L.foot - (FOOT - CAM_HEIGHT) / (dist * 2 * tv);
      fr.lookFrom.copy(cardPos).add(new THREE.Vector3(0, 0.9, -14));

      // customize close-up: the card face-on and large. Wide screens keep it left of the
      // side panel; narrow ones lift it above the bottom sheet.
      if (aspect >= 1) {
        fr.fovFocus = 34;
        const tf = Math.tan(THREE.MathUtils.degToRad(fr.fovFocus / 2));
        const dF = CARD_H / (0.74 * 2 * tf);
        const shift = dF * tf * aspect * 0.34;
        fr.focusPos.set(cardPos.x + shift, cardPos.y + 0.02, cardPos.z + dF);
        fr.focusLook.set(cardPos.x + shift, cardPos.y, cardPos.z);
      } else {
        fr.fovFocus = 50;
        const tf = Math.tan(THREE.MathUtils.degToRad(fr.fovFocus / 2));
        const dF = Math.max(CARD_H / (0.42 * 2 * tf), CARD_W / (0.78 * 2 * tf * aspect));
        const drop = dF * tf * 0.5;
        fr.focusPos.set(cardPos.x, cardPos.y - drop, cardPos.z + dF);
        fr.focusLook.set(cardPos.x, cardPos.y - drop, cardPos.z);
      }
    },

    update(s, live, fe, pointer) {
      card.update(s);
      const g = card.group;
      // the card follows the pointer more than the camera does; in the close-up it holds still enough to read
      const calm = 1 - fe * 0.85;
      g.position.copy(cardPos);
      g.position.y += Math.sin(s * 0.9) * 0.02 * live * calm;
      g.rotation.y = (Math.sin(s * 0.35) * 0.14 * calm + pointer.x * (0.58 - 0.3 * fe)) * live;
      g.rotation.x = (Math.sin(s * 0.5) * 0.03 * calm + pointer.y * (0.32 - 0.14 * fe)) * live;
    },

    setPower(power, strike) {
      cardSpot.intensity = SPOT_CD * strike;
      floorSpot.intensity = FLOOR_CD * strike;
      stripMat.color.copy(STRIP_WHITE).multiplyScalar(0.04 + 0.96 * strike);
      card.setHolo(0.25 + 0.75 * power);
    },

    // the Customize button rides the card's right-middle edge
    anchor(out) {
      out.set(CARD_W / 2 + 0.04, 0, 0.01);
      card.group.localToWorld(out);
      return true;
    },

    setConfig(cfg) {
      card.setConfig(cfg);
    },

    dispose() {
      card.dispose();
      for (const d of disposables) d.dispose();
    },
  };
}
